/**
 * Accept an invite and set a password.
 *
 * The client checks length only, matching the house policy: 12 characters
 * minimum, no composition rules (they push people toward "Password1!" while
 * blocking good passphrases). The real gate is the server, which also runs the
 * breach check. `password_breached` gets its own sentence here, because
 * "invalid password" would leave someone guessing at a rule that does not
 * exist.
 */
import { useState, type FormEvent } from 'react';
import { useLocation, useParams } from 'wouter';
import { KeyRound } from 'lucide-react';
import { api, ApiError } from '../lib/api.ts';
import { useSession } from '../lib/session.tsx';
import { Field, PandaMark, Spinner } from '../components/Bits.tsx';
import { OutcomeModal } from '../components/Overlay.tsx';

const MIN_LENGTH = 12;

export default function AcceptInvitePage() {
  const params = useParams<{ token?: string }>();
  const [, navigate] = useLocation();
  const { setSession } = useSession();
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [busy, setBusy] = useState(false);
  const [errors, setErrors] = useState<{ password?: string; confirm?: string }>({});
  const [failure, setFailure] = useState<string | null>(null);

  const token = params.token ?? '';

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    const next: typeof errors = {};
    if (password.length < MIN_LENGTH) next.password = `Use at least ${MIN_LENGTH} characters. A short phrase works well.`;
    if (confirm !== password) next.confirm = 'The two passwords do not match.';
    setErrors(next);
    if (Object.keys(next).length > 0) {
      setFailure('Some details need attention. The fields to fix are marked.');
      return;
    }
    setBusy(true);
    try {
      const me = await api.acceptInvite(token, password);
      setSession(me);
      // Leave the invite route, or the signed-in person is stranded on a form
      // whose single-use token is now spent.
      navigate('/', { replace: true });
      return;
    } catch (err) {
      const apiErr = err instanceof ApiError ? err : null;
      if (apiErr?.code === 'password_breached') {
        setErrors({ password: 'That password has appeared in a known data breach. Please choose another.' });
      } else if (apiErr?.code === 'password_too_short') {
        setErrors({ password: `Use at least ${MIN_LENGTH} characters.` });
      }
      setFailure(apiErr?.message ?? 'Could not set your password. Try again in a moment.');
    } finally {
      setBusy(false);
    }
  }

  if (!token) {
    return (
      <div className="flex min-h-screen items-center justify-center p-6">
        <div className="card card-lift max-w-md p-6 text-center">
          <h1 className="text-[17px] font-bold text-ink">That invite link looks incomplete</h1>
          <p className="mt-2 text-[13.5px] leading-relaxed text-muted">
            Open the full link from your invite, or ask your manager to send a fresh one.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen items-center justify-center px-5 py-10">
      <div className="w-full" style={{ maxWidth: 400 }}>
        <div className="mb-7 flex flex-col items-center text-center">
          <PandaMark size={58} />
          <h1 className="mt-4 text-[22px] font-extrabold tracking-tight text-ink">Set your password</h1>
          <p className="mt-1.5 text-[13.5px] leading-relaxed text-muted">
            Pick something only you know. We never send passwords by email.
          </p>
        </div>
        <form onSubmit={onSubmit} className="card card-lift p-6" noValidate>
          <Field
            label="New password"
            error={errors.password}
            hint={`At least ${MIN_LENGTH} characters. Length beats symbols, so a memorable phrase is ideal.`}
            id="invite-password"
          >
            <input
              id="invite-password"
              className="field"
              type="password"
              autoComplete="new-password"
              value={password}
              aria-invalid={errors.password ? 'true' : undefined}
              onChange={(e) => setPassword(e.target.value)}
            />
          </Field>
          <Field label="Confirm password" error={errors.confirm} id="invite-confirm">
            <input
              id="invite-confirm"
              className="field"
              type="password"
              autoComplete="new-password"
              value={confirm}
              aria-invalid={errors.confirm ? 'true' : undefined}
              onChange={(e) => setConfirm(e.target.value)}
            />
          </Field>
          <button type="submit" className="btn btn-primary btn-lg w-full" disabled={busy}>
            {busy ? <Spinner size={16} label="Saving" /> : <KeyRound size={16} aria-hidden="true" />}
            Set password and sign in
          </button>
        </form>
      </div>
      <OutcomeModal
        open={failure !== null}
        onClose={() => setFailure(null)}
        ok={false}
        title="Could not finish"
        message={failure ?? ''}
      />
    </div>
  );
}
